import { Box, CircularProgress } from '@mui/material'
import Collapse from '@mui/material/Collapse'
import {
  ASSIGNMENT_STATE_AUTO,
  ASSIGNMENT_STATE_POST,
  ASSIGNMENT_STATE_TEAM_OK,
} from 'lib/assignmentStates'
import getOption, { OPTION_ALL_VALUE } from 'lib/getOption'
import { TABLE_ROWS_PER_PAGE_OPTIONS, TABLE_WIDTH_PARTNER } from 'lib/tableLib'
import { orderBy } from 'lodash'
import T from 'prop-types'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDebouncedCallback } from 'use-debounce'

import ConfirmBar from 'components/atoms/ConfirmBar'
import DownloadBar from 'components/atoms/DownloadBar'
import FilterRange from 'components/atoms/FilterRange'
import FilterSelect from 'components/atoms/FilterSelect'
import LogIcon from 'components/atoms/LogIcon'
import Table from 'components/atoms/Table'
import TableHeaderCell from 'components/atoms/TableHeaderCell'

import useConfirmationDialog from 'hooks/useConfirmationDialog'
import useInputOutput from 'hooks/useInputOutput'
import useLogs from 'hooks/useLogs'
import useSort from 'hooks/useSort'
import useTablePaginationSync from 'hooks/useTablePaginationSync'

import AssignmentConfirmationDialog from '../Assignment/AssignmentConfirmationDialog'
import DeltaTableCell from '../Assignment/DeltaTableCell'
import {
  getAverage,
  getDelta,
  handleDeltaErrors,
} from '../Assignment/assignmentLib'
import CapField from '../SessionsCaps/CapField'

import CapManagementRecapValues from './CapManagementRecapValues'
import useCapManagementFilters from './useCapManagementFilters'

const TABLE_WIDTH_COMMON = 80
const TABLE_WIDTH_TEAM_PROPOSAL = 90

const createColumns = (
  t,
  session,
  cars,
  partners,
  allOption,
  capManagementFilters
) => {
  const {
    filters,
    filtersData,
    setAutomaticRange,
    setProposalRange,
    setCapRange,
    setIndexRange,
    setPartners,
    setPartnerGroup,
    setDelta,
    setDeltaTotal,
  } = capManagementFilters

  return [
    {
      accessor: 'partnerGroup',
      Header: (
        <TableHeaderCell
          label={t('assignment.cap-management.table.partnerGroup')}
          filter={
            <FilterSelect
              allOption={allOption}
              options={filtersData.partnersGroup}
              value={filters.partnersGroup}
              handleChange={setPartnerGroup}
              multiple
              hasSearchField
            />
          }
        />
      ),
      cellProps: {
        sx: {
          width: TABLE_WIDTH_PARTNER,
        },
      },
    },
    {
      accessor: 'name',
      Header: (
        <TableHeaderCell
          label={t('assignment.cap-management.table.partner')}
          filter={
            <FilterSelect
              allOption={allOption}
              options={filtersData.partners}
              value={filters.partners}
              handleChange={setPartners}
              multiple
              hasSearchField
            />
          }
        />
      ),
      cellProps: {
        sx: {
          width: TABLE_WIDTH_PARTNER,
        },
      },
    },
    {
      accessor: 'automatic',
      Header: (
        <TableHeaderCell
          label={t('assignment.cap-management.table.automatic')}
          filter={
            <FilterRange
              minBound={filtersData.automaticMin}
              maxBound={filtersData.automaticMax}
              min={filters.automaticMin}
              max={filters.automaticMax}
              handleRangeChange={setAutomaticRange}
            />
          }
        />
      ),
      cellProps: {
        sx: {
          width: TABLE_WIDTH_COMMON,
        },
      },
    },
    {
      accessor: 'teamProposal',
      Header: (
        <TableHeaderCell
          label={t('assignment.cap-management.table.teamProposal')}
          filter={
            <FilterRange
              minBound={filtersData.proposalMin}
              maxBound={filtersData.proposalMax}
              min={filters.proposalMin}
              max={filters.proposalMax}
              handleRangeChange={setProposalRange}
            />
          }
        />
      ),
      cellProps: {
        sx: {
          width: TABLE_WIDTH_TEAM_PROPOSAL,
        },
      },
    },
    {
      accessor: 'capField',
      Header: (
        <TableHeaderCell
          label={t('assignment.cap-management.table.cap')}
          filter={
            <FilterRange
              minBound={filtersData.capMin}
              maxBound={filtersData.capMax}
              min={filters.capMin}
              max={filters.capMax}
              handleRangeChange={setCapRange}
            />
          }
        />
      ),
      cellProps: {
        sx: {
          width: TABLE_WIDTH_COMMON,
        },
      },
    },
    {
      accessor: 'delta',
      Header: (
        <TableHeaderCell
          label={t('assignment.cap-management.table.delta')}
          errorsNumber={handleDeltaErrors(
            cars,
            partners,
            ASSIGNMENT_STATE_POST,
            session
          )}
          filter={
            <FilterSelect
              allOption={allOption}
              options={filtersData.delta}
              value={filters.delta}
              handleChange={setDelta}
            />
          }
        />
      ),
      Cell: ({ value }) => <DeltaTableCell value={value} />,
      cellProps: {
        sx: {
          width: TABLE_WIDTH_COMMON,
        },
      },
    },
    {
      accessor: 'deltaTotal',
      Header: (
        <TableHeaderCell
          label={t('assignment.cap-management.table.deltaTotal')}
          errorsNumber={handleDeltaErrors(cars, partners, null, session)}
          errorColor="warning"
          filter={
            <FilterSelect
              allOption={allOption}
              options={filtersData.deltaTotal}
              value={filters.deltaTotal}
              handleChange={setDeltaTotal}
            />
          }
        />
      ),
      Cell: ({ value }) => <DeltaTableCell value={value} />,
      cellProps: {
        sx: {
          width: TABLE_WIDTH_TEAM_PROPOSAL,
        },
      },
    },
    {
      accessor: 'avgIndex',
      Header: (
        <TableHeaderCell
          label={t('assignment.cap-management.table.avgIndex')}
          filter={
            <FilterRange
              minBound={filtersData.indexMin}
              maxBound={filtersData.indexMax}
              min={filters.indexMin}
              max={filters.indexMax}
              handleRangeChange={setIndexRange}
              step={0.5}
            />
          }
        />
      ),
      Cell: ({ value }) => value || '-',
    },
  ]
}
export default function AssignmentCapManagement({
  session,
  partners,
  cars,
  handleCapChange,
  handleValidation,
  handleConfirm,
  dateValue,
  setDateValue,
  isUpdatingSession,
  readOnly,
  contentIsReadOnly,
}) {
  const { t } = useTranslation()

  const [filteredItems, setFilteredItems] = useState([])

  const { saveLogs, isFetching: isFetchingLogs } = useLogs()

  const { saveXls } = useInputOutput()

  const { order, setSort } = useSort('partnerGroup')

  const capManagementFilters = useCapManagementFilters()

  const { filters, initFilters, filter } = capManagementFilters

  const handleFilters = useDebouncedCallback(
    (items, filters, setFilteredItems, filter) =>
      setFilteredItems(filter(items, filters)),

    100
  )

  const {
    openConfirmationDialog,
    handleConfirmConfirmationDialog,
    handleOpenConfirmationDialog,
    handleCloseConfirmationDialog,
  } = useConfirmationDialog(handleConfirm)

  const allOption = useMemo(
    () => getOption(t('common.form.all'), OPTION_ALL_VALUE),
    [t]
  )

  const columns = useMemo(
    () =>
      createColumns(
        t,
        session,
        cars,
        partners,
        allOption,
        capManagementFilters
      ),
    [t, session, cars, partners, allOption, capManagementFilters]
  )

  const preparedItems = useMemo(
    () =>
      partners.map((partner) => {
        const assignedCars = cars.filter(
          (car) => car.assignedPartnerId === partner.partnerId
        )

        const cap = session.assignmentCaps.find(
          (cap) => cap.partnerId === partner.partnerId
        )?.cap

        const delta = getDelta(
          assignedCars,
          partner,
          ASSIGNMENT_STATE_POST,
          session
        )

        const deltaTotal = assignedCars.length - cap

        const automaticLength = assignedCars.filter(
          (car) => car.assignmentState === ASSIGNMENT_STATE_AUTO
        ).length

        const teamProposalLength = assignedCars.filter((car) =>
          [ASSIGNMENT_STATE_TEAM_OK, ASSIGNMENT_STATE_POST].includes(
            car.assignmentState
          )
        ).length

        return {
          ...partner,
          name: partner.name,
          automatic: automaticLength,
          teamProposal: teamProposalLength,
          cap,
          capField: (
            <CapField
              value={cap}
              placeholder=""
              id={`${partner.partnerId}.cap`}
              handleCapChange={(e) =>
                handleCapChange(partner.partnerId, e.target.value)
              }
              InputProps={null}
              sx={{ width: '8ch' }}
              readOnly={!!session?.assignmentEndedAt}
              disabled={contentIsReadOnly}
            />
          ),
          delta,
          deltaTotal,
          avgIndex: getAverage(
            assignedCars
              .map((car) => car.appealing)
              .filter((appealing) => appealing)
          ),
        }
      }),
    [partners, cars, session, handleCapChange, contentIsReadOnly]
  )

  useEffect(() => {
    if (!preparedItems?.length || filters?.initialized) {
      return
    }

    initFilters(preparedItems)
  }, [initFilters, filters?.initialized, preparedItems.length, preparedItems])

  useEffect(() => {
    handleFilters(preparedItems, filters, setFilteredItems, filter)
  }, [filter, filters, handleFilters, partners, preparedItems])

  const sortedPartners = useMemo(
    () =>
      orderBy(
        filteredItems,
        [order?.column === 'capField' ? 'cap' : order?.column],
        [order?.direction]
      ),
    [filteredItems, order?.column, order?.direction]
  )

  const {
    page,
    rowsPerPage,
    pagedItems,
    handleChangePage,
    handleChangeRowsPerPage,
  } = useTablePaginationSync({ items: sortedPartners })

  const handleDownload = async () => {
    const titlesRow = [
      t('common.excel.partner'),
      t('common.excel.automatic'),
      t('common.excel.teamProposal'),
      t('common.excel.cap'),
      t('common.excel.delta'),
      t('common.excel.deltaTotal'),
      t('common.excel.avgIndex'),
    ]

    const rows = [
      titlesRow,
      ...preparedItems.map((partner) => [
        partner.name,
        partner.automatic,
        partner.teamProposal,
        partner.cap,
        partner.delta,
        partner.deltaTotal,
        partner.avgIndex || '-',
      ]),
    ]

    saveXls('cap-management', rows)
  }

  return (
    <>
      <Box mt={1} data-testid="assignment-cap" />
      <ConfirmBar
        label={t('assignment.car-assignment.confirmButton')}
        left={<CapManagementRecapValues cars={cars} />}
        right={
          <>
            <Collapse orientation="horizontal" in={isFetchingLogs}>
              <div style={{ marginLeft: 4, marginRight: 4 }}>
                <CircularProgress size={20} />
              </div>
            </Collapse>
            <LogIcon
              disabled={isFetchingLogs}
              handleClick={() =>
                saveLogs(`logs_assignment_${session?.sessionId}`, {
                  area: t('common.logs.assignment'),
                  sessionId: session?.sessionId,
                })
              }
            />
            <DownloadBar handleDownload={handleDownload} />
          </>
        }
        disabled={
          !handleValidation() ||
          !!session?.assignmentEndedAt ||
          isUpdatingSession ||
          readOnly ||
          contentIsReadOnly
        }
        handleConfirm={handleOpenConfirmationDialog}
      />
      <Box pb={2} />
      <Table
        items={pagedItems}
        columns={columns}
        total={filteredItems.length}
        hasPagination={true}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={TABLE_ROWS_PER_PAGE_OPTIONS}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        sortable
        orderColumn={order?.column}
        orderDirection={order?.direction}
        handleSort={setSort}
      />
      <AssignmentConfirmationDialog
        open={openConfirmationDialog}
        handleClose={handleCloseConfirmationDialog}
        handleSubmit={handleConfirmConfirmationDialog}
        dateValue={dateValue}
        setDateValue={setDateValue}
      />
    </>
  )
}

AssignmentCapManagement.propTypes = {
  session: T.object.isRequired,
  partners: T.array,
  cars: T.array,
  handleCapChange: T.func,
  handleValidation: T.func.isRequired,
  handleConfirm: T.func,
  dateValue: T.instanceOf(Date),
  setDateValue: T.func,
  isUpdatingSession: T.bool,
  readOnly: T.bool,
  contentIsReadOnly: T.bool,
}
